/**
 * Generic registry factory — string-keyed, type-safe, frozen after bootstrap.
 *
 * Used by:
 * - FormatRegistry (pure display transforms, keyed by formatKey string from JSON)
 * - AdapterRegistry (form-engine internal — field type → React component)
 *
 * NOT used by ActionRegistry (that lives in @packages/table-builder and has
 * its own decorator-based registration pattern).
 */

export interface Registry<T> {
  register(key: string, value: T): void;
  get(key: string): T | undefined;
  has(key: string): boolean;
  keys(): string[];
  freeze(): void;
}

export function createRegistry<T>(name: string): Registry<T> {
  const store = new Map<string, T>();
  let frozen = false;

  return {
    register(key: string, value: T): void {
      if (frozen) {
        throw new Error(
          `[${name}] Cannot register "${key}" — registry is frozen. ` +
            `All registrations must happen during module initialization.`,
        );
      }
      if (store.has(key)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[${name}] Overwriting existing key "${key}"`);
        }
      }
      store.set(key, value);
    },

    get(key: string): T | undefined {
      return store.get(key);
    },

    has(key: string): boolean {
      return store.has(key);
    },

    keys(): string[] {
      return Array.from(store.keys());
    },

    freeze(): void {
      frozen = true;
    },
  };
}
